import {
	Category,
	CategoryDto,
	InstitutionDto,
	ProductDto,
	ProductTypeDto,
	TariffDto,
} from "Infrastructure/Models/Messages/Dto/ProductStatus";
import {
	DeviceEnabledDisabledModel,
	ProductDeviceEnabledDisabledModel,
} from "./DeviceUtils";
import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";

export interface ProductStatusDeviceModel {
	institutions: InstitutionDeviceModel[];
	categories: CategoryDeviceModel[];
	productTypes: ProductTypeDeviceModel[];
	products: ProductDeviceModel[];
	tariffs: TariffDeviceModel[];
}

export interface InstitutionDeviceModel {
	institution: InstitutionDto;
	devices: DeviceEnabledDisabledModel[];
}

export interface CategoryDeviceModel {
	category: Category;
	devices: DeviceEnabledDisabledModel[];
}

export interface ProductTypeDeviceModel {
	productType: ProductTypeDto;
	devices: DeviceEnabledDisabledModel[];
}

export interface ProductDeviceModel {
	product: ProductDto;
	devices: ProductDeviceEnabledDisabledModel[];
}

export interface TariffDeviceModel {
	tariff: TariffDto;
	devices: DeviceEnabledDisabledModel[];
}

export function mergeDeviceStatuses(
	deviceStatuses: DeviceStatus[] | null,
	deviceStatus?: DeviceStatus | undefined
): ProductStatusDeviceModel | null {
	if (!deviceStatuses) {
		return null;
	}

	const institutionsMap = new Map<number, InstitutionDeviceModel>();
	const categoriesMap = new Map<number, CategoryDeviceModel>();
	const productTypesMap = new Map<number, ProductTypeDeviceModel>();
	const productsMap = new Map<number, ProductDeviceModel>();
	const tariffsMap = new Map<number, TariffDeviceModel>();

	const filteredStatuses = deviceStatuses?.filter(
		(x) =>
			!x.isSelfInsurance &&
			//x.deviceState === DeviceState.Connected &&
			(!!deviceStatus ? x === deviceStatus : true)
	) ?? [];

	for (const status of filteredStatuses) {
		const deviceName = status.deviceName;

		// Add institutions
		if (status?.productStatus?.institutions) {
			for (const institution of status.productStatus.institutions) {
				if (!institutionsMap.has(institution.institutionID)) {
					institutionsMap.set(institution.institutionID, {
						institution,
						devices: [],
					});
				}
				const existingInstitution = institutionsMap.get(
					institution.institutionID
				)!;
				if (
					!existingInstitution.devices.some(
						(d) => d.deviceName === deviceName
					)
				) {
					existingInstitution.devices.push({
						deviceName,
						isEnabled: true,
					});
				}
			}
		}

		// Add categories
		if (status?.productStatus?.categories) {
			for (const category of status.productStatus.categories) {
				if (!categoriesMap.has(category.categoryID)) {
					categoriesMap.set(category.categoryID, {
						category,
						devices: [],
					});
				}
				categoriesMap.get(category.categoryID)!.devices.push({
					deviceName,
					isEnabled: category.isActive,
				});
			}
		}

		// Add product types
		if (status?.productStatus?.productTypes) {
			for (const productType of status.productStatus.productTypes) {
				if (!productTypesMap.has(productType.productTypeID)) {
					productTypesMap.set(productType.productTypeID, {
						productType,
						devices: [],
					});
				}
				productTypesMap.get(productType.productTypeID)!.devices.push({
					deviceName,
					isEnabled: productType.isActive,
				});
			}
		}

		// Add products
		if (status?.productStatus?.products) {
			for (const product of status.productStatus.products) {
				if (!productsMap.has(product.productID)) {
					productsMap.set(product.productID, {
						product,
						devices: [],
					});
				}
				productsMap.get(product.productID)!.devices.push({
					deviceName,
					isEnabled: product.isActive,
					isContractCreateEnabled: product.isContractCreateEnabled,
				});
			}
		}

		// Add tariffs
		if (status?.productStatus?.tariffs) {
			for (const tariff of status.productStatus.tariffs) {
				if (!tariffsMap.has(tariff.tariffID)) {
					tariffsMap.set(tariff.tariffID, {
						tariff,
						devices: [],
					});
				}
				tariffsMap.get(tariff.tariffID)!.devices.push({
					deviceName,
					isEnabled: tariff.isActive,
				});
			}
		}
	}

	return {
		institutions: Array.from(institutionsMap.values()),
		categories: Array.from(categoriesMap.values()),
		productTypes: Array.from(productTypesMap.values()),
		products: Array.from(productsMap.values()),
		tariffs: Array.from(tariffsMap.values()),
	};
}

export const getProductTypesDisplayName = (productTypes: string): string => {
	const parsedProductTypes = productTypes.split(";").map(Number);
	return parsedProductTypes.map((x) => getProductTypeDisplayName(x)).join("; ");
};

export const getProductTypeDisplayName = (productTypeID: ProductTypeIDs) => {
	switch (productTypeID) {
		case ProductTypeIDs.LiabilityInsurance:
			return ProductTypeTranslations.VehicleInsurance.LiabilityInsurance;
		case ProductTypeIDs.CollisionDamageInsurance:
			return ProductTypeTranslations.VehicleInsurance.CollisionDamageInsurance;
		case ProductTypeIDs.VehicleInsurance:
			return ProductTypeTranslations.VehicleInsurance.VehicleInsurance;
		case ProductTypeIDs.PropertyInsurance:
			return ProductTypeTranslations.PropertyInsurance.PropertyInsurance;
		case ProductTypeIDs.HouseholdInsurance:
			return ProductTypeTranslations.PropertyInsurance.HouseholdInsurance;
		case ProductTypeIDs.PropertyAndHouseholdInsurance:
			return ProductTypeTranslations.PropertyInsurance
				.PropertyAndHouseholdInsurance;
		case ProductTypeIDs.SingleTravel:
			return ProductTypeTranslations.TravelInsurance.SingleTravel;
		case ProductTypeIDs.MultipleTravelsInYear:
			return ProductTypeTranslations.TravelInsurance.MultipleTravelsInYear;
		case ProductTypeIDs.AnnualStay:
			return ProductTypeTranslations.TravelInsurance.AnnualStay;
		case ProductTypeIDs.ThirdPartyInsurance:
			return ProductTypeTranslations.LiabilityInsurance.ThirdPartyInsurance;
		case ProductTypeIDs.EmployeeResponsibility:
			return ProductTypeTranslations.LiabilityInsurance.EmployeeResponsibility;
		case ProductTypeIDs.LifeInsurance:
			return ProductTypeTranslations.LifeInsurance.LifeInsurance;
		case ProductTypeIDs.LifeInsuranceMethodology:
			return ProductTypeTranslations.LifeInsurance.LifeInsuranceMethodology;
		case ProductTypeIDs.MortgageClientCredit:
			return ProductTypeTranslations.MortgageComparison.MortgageClientCredit;
		case ProductTypeIDs.MortgagePaymentComparison:
			return ProductTypeTranslations.MortgageComparison
				.MortgagePaymentComparison;
		case ProductTypeIDs.MortgagePaymentAndClientCredit:
			return ProductTypeTranslations.MortgageComparison
				.MortgagePaymentAndClientCredit;
		case ProductTypeIDs.MortgageAmericanMortgage:
			return ProductTypeTranslations.MortgageComparison
				.MortgageAmericanMortgage;
		case ProductTypeIDs.MortgageBuildingSavings:
			return ProductTypeTranslations.MortgageComparison.MortgageBuildingSavings;
		case ProductTypeIDs.MortgageComparisonConsumerCredit:
			return ProductTypeTranslations.MortgageComparison
				.MortgageComparisonConsumerCredit;
		case ProductTypeIDs.MortgageMethodology:
			return ProductTypeTranslations.MortgageMethodology;
		case ProductTypeIDs.MortgageCalculator:
			return ProductTypeTranslations.MortgageCalculator;
		case ProductTypeIDs.MortgageClientCreditCredibility:
			return ProductTypeTranslations.MortgageComparison
				.MortgageClientCreditCredibility;
		case ProductTypeIDs.RepayAbilityInsurance:
			return ProductTypeTranslations.RepayAbility;
		case ProductTypeIDs.Investment:
			return ProductTypeTranslations.Investment;
		case ProductTypeIDs.VehicleHavMethodology:
			return ProductTypeTranslations.Methodology.VehicleHav;
		case ProductTypeIDs.VehiclePovMethodology:
			return ProductTypeTranslations.Methodology.VehiclePov;
		case ProductTypeIDs.PropertyMethodology:
			return ProductTypeTranslations.Methodology.PropertyHouse;
		case ProductTypeIDs.FlatMethodology:
			return ProductTypeTranslations.Methodology.PropertyFlat;
		case ProductTypeIDs.HouseholdMethodology:
			return ProductTypeTranslations.Methodology.PropertyHousehold;
		case ProductTypeIDs.MortgageAmericanMethodology:
			return ProductTypeTranslations.Methodology.MortgageAmerican;
		case ProductTypeIDs.MortgageMethodologyBuildingSavings:
			return ProductTypeTranslations.Methodology.Building;
		case ProductTypeIDs.MortgageMethodologyConsumerCredit:
			return ProductTypeTranslations.Methodology.ConsumerCredit;
		case ProductTypeIDs.TravelMethodology:
			return ProductTypeTranslations.Methodology.Travel;
		case ProductTypeIDs.VehicleAssistanceInsurance:
			return ProductTypeTranslations.Assistance.Vehicle;
		case ProductTypeIDs.TravelAssistanceInsurance:
			return ProductTypeTranslations.Assistance.Travel;
		case ProductTypeIDs.LifeCalculator:
			return ProductTypeTranslations.LifeInsurance.LifeCalculator;
		case ProductTypeIDs.DogInsurance:
			return ProductTypeTranslations.Pet.Dog;
		case ProductTypeIDs.CatInsurance:
			return ProductTypeTranslations.Pet.Cat;
		case ProductTypeIDs.SmeInsurance:
			return ProductTypeTranslations.Sme.SmeInsurance;
		case ProductTypeIDs.VehiclePersonalAccidentInsurance:
			return ProductTypeTranslations.VehiclePersonalAccident.VehiclePersonalAccidentInsurance;
		default:
			return null;
	}
};

export enum ProductTypeIDs {
	LiabilityInsurance = 1,
	CollisionDamageInsurance = 2,
	VehicleInsurance = 3,
	PropertyInsurance = 4,
	HouseholdInsurance = 5,
	PropertyAndHouseholdInsurance = 6,
	SingleTravel = 7,
	MultipleTravelsInYear = 8,
	AnnualStay = 9,
	ThirdPartyInsurance = 10,
	LifeInsurance = 11,
	MortgagePaymentComparison = 12,
	MortgageClientCredit = 13,
	MortgagePaymentAndClientCredit = 14,
	MortgageMethodology = 15,
	MortgageCalculator = 16,
	MortgageClientCreditCredibility = 17,
	LifeInsuranceMethodology = 18,
	MortgageMethodologyBuildingSavings = 19,
	ThirdPartyMethodology = 20,
	VehiclePovMethodology = 21,
	VehicleHavMethodology = 22,
	PropertyMethodology = 23,
	HouseholdMethodology = 24,
	FlatMethodology = 25,
	MortgageBuildingSavings = 26,
	MortgageAmericanMortgage = 27,
	MortgageAmericanMethodology = 28,
	MortgageMethodologyConsumerCredit = 29,
	MortgageComparisonConsumerCredit = 30,
	EmployeeResponsibility = 31,
	RepayAbilityInsurance = 32,
	TravelMethodology = 33,
	Investment = 34,
	VehicleAssistanceInsurance = 35,
	TravelAssistanceInsurance = 36,
	LifeCalculator = 37,
	DogInsurance = 38,
	CatInsurance = 39,
	SmeInsurance = 40,
	VehiclePersonalAccidentInsurance = 43,
}

const ProductTypeTranslations = {
	VehicleInsurance: {
		LiabilityInsurance: "Povinné ručení",
		CollisionDamageInsurance: "Havarijní pojištění",
		VehicleInsurance: "Povinné ručení a havarijní pojištění",
	},
	PropertyInsurance: {
		PropertyInsurance: "Pojištění nemovitosti",
		HouseholdInsurance: "Pojištění domácnosti",
		PropertyAndHouseholdInsurance: "Pojištění nemovitosti a domácnosti",
	},
	TravelInsurance: {
		SingleTravel: "Jedna cesta",
		MultipleTravelsInYear: "Více cest na rok",
		AnnualStay: "Roční pobyt",
	},
	LiabilityInsurance: {
		ThirdPartyInsurance: "Pojištění odpovědnosti",
		EmployeeResponsibility: "Pojištění zaměstnanecké odpovědnosti",
	},
	LifeInsurance: {
		LifeInsurance: "Životní pojištění",
		LifeInsuranceMethodology: "Životní pojištění - Metodika",
		LifeCalculator: "Životní pojištění - Kalkulačka TNÚ"
	},
	MortgageComparison: {
		MortgageClientCredit: "Hypotéky - Bonita klienta",
		MortgagePaymentComparison: "Hypotéky - Porovnáni splátek",
		MortgagePaymentAndClientCredit: "Hypotéky",
		MortgageBuildingSavings: "Stavební spoření",
		MortgageAmericanMortgage: "Americké hypotéky",
		MortgageComparisonConsumerCredit: "Spotřebitelské úvěry",
		MortgageClientCreditCredibility: "Bonita klienta",
	},
	MortgageMethodology: "Hypotéky - Metodika",
	MortgageCalculator: "Splátkový kalendář",
	RepayAbility: "Pojištění schopnosti splácet",
	Investment: "Investice",
	Methodology: {
		VehiclePov: "Povinné ručení - Metodika",
		VehicleHav: "Havarijní pojištění - Metodika",
		PropertyHouse: "Pojištění rodinných domů - Metodika",
		PropertyHousehold: "Pojištění domácnosti - Metodika",
		PropertyFlat: "Pojištění bytových jednotek - Metodika",
		Travel: "Cestovní pojištění - Metodika",
		Life: "Životní pojištění - Metodika",
		Building: "Stavební spoření - Metodika",
		MortgageAmerican: "Americké hypotéky - Metodika",
		ConsumerCredit: "Spotřebitelské úvěry - Metodika",
	},
	Assistance: {
		Vehicle: "Pojištění vozidla - Asistenční služby",
		Travel: "Cestovní pojištění - Asistenční služby",
	},
	Pet: {
		Dog: "Pojištění mazlíčků - Pes",
		Cat: "Pojištění mazlíčků - Kočka",
	},
	Sme: {
		SmeInsurance: "Pojištění podnikání",
	},
	VehiclePersonalAccident: {
		VehiclePersonalAccidentInsurance: "Úrazové pojištění osob k vozidlu",
	},
};

export const getCategoryDisplayName = (categoryID: CategoryIDs) => {
	switch (categoryID) {
		case CategoryIDs.Vehicle:
			return CategoryTranslations.Car;
		case CategoryIDs.Property:
			return CategoryTranslations.Property;
		case CategoryIDs.Travel:
			return CategoryTranslations.Travel;
		case CategoryIDs.Liability:
			return CategoryTranslations.Liability;
		case CategoryIDs.Life:
			return CategoryTranslations.Life;
		case CategoryIDs.Mortgage:
			return CategoryTranslations.Mortgage;
		case CategoryIDs.RepayAbility:
			return CategoryTranslations.RepayAbility;
		case CategoryIDs.Investment:
			return CategoryTranslations.Investment;
		case CategoryIDs.Assistance:
			return CategoryTranslations.Assistance;
		case CategoryIDs.Pet:
			return CategoryTranslations.Pet;
		case CategoryIDs.Sme:
			return CategoryTranslations.Sme;
		default:
			return `CategoryID.${(categoryID as number).toString()}`;
	}
};

export enum CategoryIDs {
	Vehicle = 1,
	Property = 2,
	Travel = 3,
	Liability = 4,
	Life = 5,
	Mortgage = 6,
	RepayAbility = 7,
	Investment = 8,
	Assistance = 9,
	Pet = 10,
	Sme = 11,
}

const CategoryTranslations = {
	Car: "Auto",
	Property: "Majetek",
	Travel: "Cestovní",
	Liability: "Odpovědnost",
	Life: "Životní",
	Mortgage: "Úvěry",
	RepayAbility: "Splátky",
	Investment: "Investice",
	Assistance: "Asistence",
	Pet: "Mazlíčci",
	Sme: "Podnikání",
};
